import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase } from '../utils/api';

const SAMLSSOButton = ({ email, onError, idpName: propIdpName, primary = false }) => {
  const [loading, setLoading] = useState(false);
  const [samlAvailable, setSamlAvailable] = useState(!!propIdpName);
  const [idpName, setIdpName] = useState(propIdpName || 'Enterprise SSO');
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    // If idpName was provided as a prop, SAML is already confirmed available
    if (propIdpName) {
      setSamlAvailable(true);
      setIdpName(propIdpName);
      return;
    }

    // Otherwise, check if SAML is available for this email domain
    const checkSAML = async () => {
      if (!email || !email.includes('@')) {
        setSamlAvailable(false);
        return;
      }

      setChecking(true);
      try {
        const apiBase = getApiBase();
        const response = await axios.get(`${apiBase}/auth/saml/check`, {
          params: { email }
        });

        if (response.data.saml_enabled) {
          setSamlAvailable(true);
          setIdpName(response.data.idp_name || 'Enterprise SSO');
        } else {
          setSamlAvailable(false);
        }
      } catch (error) {
        console.error('Error checking SAML availability:', error);
        setSamlAvailable(false);
      } finally {
        setChecking(false);
      }
    };

    // Debounce the check
    const timeoutId = setTimeout(checkSAML, 500);
    return () => clearTimeout(timeoutId);
  }, [email, propIdpName]);

  const handleSAMLLogin = async () => {
    if (!email || !email.includes('@')) {
      onError?.('Please enter your email address first');
      return;
    }

    setLoading(true);
    try {
      const apiBase = getApiBase();

      // Get the redirect URL from the backend
      // After SAML auth, redirect to home page (auth data is stored in localStorage by callback)
      const response = await axios.get(`${apiBase}/auth/saml/login`, {
        params: {
          email,
          redirect_url: window.location.origin
        }
      });

      if (response.data.redirect_url) {
        // Redirect to IdP
        window.location.href = response.data.redirect_url;
      } else {
        throw new Error('No redirect URL received');
      }
    } catch (error) {
      console.error('SAML login error:', error);
      const message = error.response?.data?.detail || error.message || 'Failed to initiate SAML login';
      onError?.(message);
      setLoading(false);
    }
  };

  // Only show if SAML is available for this email domain
  if (!samlAvailable && !checking) {
    return null;
  }

  // Primary style (prominent) vs secondary style
  const buttonClass = primary
    ? "group relative w-full flex items-center justify-center py-3 px-4 border-2 border-purple-600 text-sm font-semibold rounded-lg text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 transition-colors"
    : "group relative w-full flex items-center justify-center py-2.5 px-4 border border-purple-300 text-sm font-medium rounded-md text-purple-700 bg-white hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 transition-colors";

  return (
    <button
      type="button"
      onClick={handleSAMLLogin}
      disabled={loading || checking || !samlAvailable}
      className={buttonClass}
    >
      <svg
        className={`w-5 h-5 mr-2 ${primary ? 'text-white' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
        />
      </svg>
      {loading ? (
        'Redirecting to SSO...'
      ) : checking ? (
        'Checking SSO...'
      ) : (
        'SAML Login'
      )}
    </button>
  );
};

export default SAMLSSOButton;
